// Card-style row (report §2.2): a large thumbnail on the left, then a content
// block (title on top, metadata beneath). The product name is never truncated
// or hidden — it owns the top of the content block and wraps if long.
//
// `.root` is applied to the shared `TableRow` (a `.row` flex div). The tripled
// selector (specificity (0,3,0)) beats the shared `.row { align-items:center }`
// and `.row:not(.isHeader) { height:52px }` rules regardless of import order,
// so the card can size itself and align its thumbnail to the top.
.root.root.root {
  align-items: center;
  gap: 12px;
  height: auto;
  min-height: 72px;
  padding: 10px 16px;
}

.clickable {
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background: var(--color-secondary);
  }
}

// Single-select source highlight (e.g. recommendation explorer).
// Multi-select uses the checkbox path and does not need this.
.source-selected {
  background: var(--color-secondary);
  box-shadow: inset 3px 0 0 var(--color-primary, #2563eb);
}

// Rows carrying quick actions anchor the hover overlay.
.has-actions {
  position: relative;
}

// The action bar floats over the row's right edge — revealed on hover (or
// keyboard focus) so it never consumes width in the card.
.actions-overlay {
  align-items: flex-start;
  background: linear-gradient(to right, transparent, var(--color-secondary) 28px);
  display: flex;
  opacity: 0;
  padding: 12px 8px 0 32px;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  transition: opacity 0.12s;
}

.clickable:hover .actions-overlay,
.actions-overlay:focus-within {
  opacity: 1;
  pointer-events: auto;
}

// Large thumbnail. `position: relative` anchors the selection checkbox in its
// top-left corner (overlay), matching the reference card.
.thumb {
  align-items: center;
  background: var(--color-secondary);
  border-radius: 8px;
  display: flex;
  flex-shrink: 0;
  height: 60px;
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: 60px;
}

.thumb-img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.thumb-placeholder {
  color: var(--color-muted);
}

// Small checkbox pinned to the thumbnail's top-left corner. The shared
// `Checkbox` adds its own hit-area padding and a filled `--color-secondary`
// plate when checked; neither suits a corner overlay, so strip both here by
// targeting the rendered <label> (element selector — survives CSS-module
// hashing). Background stays transparent in BOTH states (checked included).
.select-box {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  left: 4px;
  position: absolute;
  top: 4px;

  label {
    background: transparent;
    padding: 0;
  }

  input {
    height: 15px;
    width: 15px;
  }
}

// Content block beside the thumbnail: title line, then the meta line.
.content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

// Title line: product name + optional status badge (e.g. "CMS Hidden") beside
// it (legacy parity — the catalog table shows the badge next to the title).
// Wraps so a long title pushes the badge onto the next line instead of clipping.
.title-row {
  align-items: center;
  column-gap: 8px;
  display: flex;
  flex-wrap: wrap;
  min-width: 0;
  row-gap: 4px;
}

// Product name — prominent, always fully shown; wraps to at most two lines
// (clamped) rather than truncating to "Cont…" / "Praia…" (report §2.2 / P0).
.title-text {
  -webkit-box-orient: vertical;
  display: -webkit-box;
  font-size: 15px;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  min-width: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  white-space: normal;
}

// Meta line: location · category · duration · price as inline icon+text pairs
// that wrap onto a second line on narrow viewports instead of clipping.
.meta {
  align-items: center;
  column-gap: 20px;
  display: flex;
  flex-wrap: wrap;
  row-gap: 4px;
}

.meta-item {
  align-items: center;
  display: inline-flex;
  gap: 6px;
  min-width: 0;
}

.meta-icon {
  color: var(--color-muted);
  flex-shrink: 0;
}

.location {
  align-items: center;
  display: flex;
  gap: 4px;
}
